import React, { useEffect, useMemo, useState } from "react";
import ShuffleIcon from "@mui/icons-material/Shuffle";
import { Box, IconButton, Typography } from "@mui/material";
import { ACCENT, SURFACE, TEXT } from "../../dark-theme/palette";
import { usePagesTranslation } from "../locales";

const TILE = 44;

function shuffleTiles(word) {
    const tiles = String(word || "")
        .toUpperCase()
        .split("")
        .filter((letter) => /[A-Z]/.test(letter))
        .map((letter, index) => ({
            id: `${index}-${letter}-${Math.random().toString(16).slice(2)}`,
            letter,
        }));
    for (let i = tiles.length - 1; i > 0; i -= 1) {
        const j = Math.floor(Math.random() * (i + 1));
        [tiles[i], tiles[j]] = [tiles[j], tiles[i]];
    }
    return tiles;
}

function assignTiles(tiles, draft) {
    const used = new Set();
    const slots = String(draft || "").split("").map((letter, index) => {
        const tile = tiles.find((item) => item.letter === letter && !used.has(item.id));
        if (tile) {
            used.add(tile.id);
            return tile;
        }
        return { id: `typed-${index}-${letter}`, letter, typed: true };
    });
    return {
        slots,
        bank: tiles.filter((tile) => !used.has(tile.id)),
    };
}

export default function TutorsPlayScramble({ wordKey, word, draft, found, onDraftChange }) {
    const { t } = usePagesTranslation();
    const [tiles, setTiles] = useState([]);

    useEffect(() => {
        setTiles(wordKey ? shuffleTiles(word) : []);
    }, [word, wordKey]);

    const letters = useMemo(
        () => String(word || "").toUpperCase().split("").filter((letter) => /[A-Z]/.test(letter)),
        [word],
    );
    const { slots, bank } = useMemo(() => assignTiles(tiles, draft), [draft, tiles]);
    const locked = Boolean(found);

    const playLetter = (letter) => {
        if (locked || !letter) {
            return;
        }
        onDraftChange(`${draft || ""}${letter}`.slice(0, letters.length));
    };

    const removeAt = (index) => {
        if (locked) {
            return;
        }
        const chars = String(draft || "").split("");
        chars.splice(index, 1);
        onDraftChange(chars.join(""));
    };

    if (!wordKey || letters.length === 0) {
        return (
            <Typography variant="body2" sx={{ color: TEXT.secondary, mt: 2 }}>
                {t("tutors.play-pick-word", "Select a word on the grid or in Word Clues.")}
            </Typography>
        );
    }

    return (
        <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 1.25, maxWidth: "100%" }}>
            {locked ? null : (
                <Box
                    sx={{
                        p: 1.25,
                        borderRadius: 2,
                        border: `1px solid ${SURFACE.borderStrong}`,
                        bgcolor: SURFACE.card,
                    }}
                >
                    <Typography variant="caption" sx={{ color: TEXT.secondary, fontWeight: 700, display: "block", mb: 1 }}>
                        {t("tutors.play-scramble", "Scrambled letters")}
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, alignItems: "center" }}>
                        {bank.map((tile) => (
                            <Box
                                key={tile.id}
                                component="button"
                                type="button"
                                onClick={() => playLetter(tile.letter)}
                                aria-label={tile.letter}
                                sx={{
                                    width: TILE,
                                    height: TILE,
                                    minWidth: TILE,
                                    minHeight: TILE,
                                    borderRadius: 1,
                                    border: `1px solid ${ACCENT.main}`,
                                    bgcolor: ACCENT.soft,
                                    color: TEXT.primary,
                                    fontWeight: 800,
                                    fontSize: "1.05rem",
                                    cursor: "pointer",
                                    p: 0,
                                }}
                            >
                                {tile.letter}
                            </Box>
                        ))}
                        <IconButton
                            aria-label={t("tutors.play-shuffle", "Shuffle letters")}
                            onClick={() => setTiles(shuffleTiles(word))}
                            sx={{ width: TILE, height: TILE, minWidth: TILE, minHeight: TILE, color: ACCENT.light }}
                        >
                            <ShuffleIcon />
                        </IconButton>
                    </Box>
                </Box>
            )}
            <Box
                sx={{
                    p: 1.25,
                    borderRadius: 2,
                    border: `1px solid ${SURFACE.borderStrong}`,
                    bgcolor: SURFACE.card,
                }}
            >
                <Typography variant="caption" sx={{ color: TEXT.secondary, fontWeight: 700, display: "block", mb: 1 }}>
                    {t("tutors.play-letter-boxes", "Play the letters")}
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                    {letters.map((_, index) => {
                        const slot = slots[index];
                        return (
                            <Box
                                key={`${wordKey}-slot-${index}`}
                                component="button"
                                type="button"
                                disabled={locked || !slot}
                                onClick={() => removeAt(index)}
                                aria-label={slot?.letter || t("tutors.play-empty-slot", "Empty letter box")}
                                sx={{
                                    width: TILE,
                                    height: TILE,
                                    minWidth: TILE,
                                    minHeight: TILE,
                                    borderRadius: 1,
                                    border: `1px solid ${slot ? ACCENT.main : SURFACE.border}`,
                                    bgcolor: "#fff",
                                    color: "#111",
                                    fontWeight: 800,
                                    fontSize: "1.05rem",
                                    cursor: locked || !slot ? "default" : "pointer",
                                    p: 0,
                                }}
                            >
                                {slot?.letter || ""}
                            </Box>
                        );
                    })}
                </Box>
            </Box>
        </Box>
    );
}
